import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  engagementCreateListBodyMapper,
  ListUsersService,
  mapCommercialProductReferencesToEngagement,
  mapEngagementListToLegacy,
  mapEngagementSystemToLegacy,
} from '@epicuro-next/data/engagement/engagement-api-utils';
import {
  BulkAddProductsQuery,
  BulkAddProductsRequestDto,
  EngagementListsWithUser,
  EngagementProductListWithUser,
  ENGAGEMENT_API_TOKEN,
  IList,
  ProductListBaseAddRequestDto,
  ProductListBulkDeleteRequestDto,
  ProductListBulkDeleteResponseDto,
  ProductListChangeProductRequestDto,
  ProductListCloneRequestDto,
  ProductListDeleteRequestDto,
  ProductListGetItemsResponseDto,
  ProductListGetRequestDto,
  ProductListGroupingType,
  ProductListReOrderProductRequestDto,
  ProductListResponseDto,
  ProductListResponseDtoPageResponseDto,
  ProductListsGetRequestDto,
  ProductListType,
  ProductListUpdateRequestDto,
  ProductReferenceDto,
} from '@epicuro-next/data/engagement/engagement-models';
import { UsersApiService } from '@epicuro-next/data/users';
import { MediaPart } from '@epicuro-next/models/engagement';
import { PulseOrderType } from 'models-common';
import { Observable, of, throwError } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { ProductsAvailabilityApi } from '../../services';
import {
  extractPatchItemsPayload,
  mapFiltersFacets,
  mapListByIdsToLegacy,
} from '../../utils';

@Injectable()
export class ProductListApiService {
  private readonly resource: string;

  constructor(
    private http: HttpClient,
    private listUsersService: ListUsersService,
    private usersApiService: UsersApiService,
    private productsAvailabilityApi: ProductsAvailabilityApi,
    @Inject(ENGAGEMENT_API_TOKEN) private readonly baseUrl: string,
  ) {
    this.resource = `${this.baseUrl}/productlist`;
  }

  public fetch(
    payload: ProductListsGetRequestDto,
  ): Observable<EngagementListsWithUser> {
    const params = new HttpParams()
      .set('page', payload?.page ?? 0)
      .set('pageSize', payload?.size ?? 10);

    return this.http
      .get<ProductListResponseDtoPageResponseDto>(`${this.resource}`, {
        params,
      })
      .pipe(
        switchMap((resp) => {
          if (!resp.items || resp.items?.length === 0) {
            return of({ userEntities: [], resp });
          }

          const externalIds = resp.items.map((item) => item.createdByUserId);

          return this.usersApiService.getUsersByExternalId(externalIds).pipe(
            map((users) => ({
              userEntities: users.map((user) => ({
                userExternalId: user.externalId,
                user,
              })),
              resp,
            })),
          );
        }),
        switchMap(({ userEntities, resp }) =>
          this.productsAvailabilityApi
            .loadAvailabilities({
              lists: resp.items ?? [],
              accountId: payload.accountId ?? '',
              siteId: payload?.siteId ?? '',
            })
            .pipe(
              map((items) => ({
                userEntities,
                resp: {
                  ...resp,
                  items,
                },
              })),
            ),
        ),
        map(({ userEntities, resp }) => {
          return mapEngagementSystemToLegacy(resp, userEntities);
        }),
      );
  }

  public get({
    productListId,
    accountId,
    siteId,
  }: ProductListGetRequestDto): Observable<EngagementProductListWithUser> {
    const request$ = this.http.get<ProductListResponseDto>(
      `${this.resource}/${productListId}`,
    );

    return this.listWithUsers(request$, accountId, siteId ?? '');
  }

  public create({
    accountId,
    siteId,
    ...body
  }: ProductListBaseAddRequestDto): Observable<EngagementProductListWithUser> {
    const payload = engagementCreateListBodyMapper(body);

    if ('type' in payload && payload.type === ProductListType.Commercial) {
      payload.productReferences = mapCommercialProductReferencesToEngagement(
        body.productReferences,
      );
    }

    const request$ = this.http.post<ProductListResponseDto>(
      `${this.resource}`,
      payload,
    );

    return this.listWithUsers(request$, accountId, siteId ?? '');
  }

  public update({
    list,
    accountId,
    siteId,
    media,
  }: ProductListUpdateRequestDto): Observable<EngagementProductListWithUser> {
    if (!list.id) {
      return throwError(() => new Error('List doesnt have ID'));
    }

    const payload: Omit<ProductListBaseAddRequestDto, 'accountId'> = {
      name: list.name as string,
      description: list.description,
      orderType: list?.orderType,
      layout: list.layout,
      type: list.type,
    };

    if (media) {
      payload.media = [
        {
          name: media.name as string,
          contentType: media.contentType,
          url: media.url,
          mediaType: media.mediaType,
          metadata: media?.metadata,
          mediaPartType: MediaPart.MainImage,
          order: 0,
        },
      ];
    }

    const request$ = this.http.put<ProductListResponseDto>(
      `${this.resource}/${list.id}`,
      engagementCreateListBodyMapper(payload),
    );
    return this.listWithUsers(request$, accountId, siteId ?? '');
  }

  public deleteMedia(listId: string, mediaId: number) {
    return this.http.delete(`${this.resource}/${listId}/media/${mediaId}`);
  }

  public changeProducts({
    id,
    patchItems,
    orderType,
    stylesOnly,
  }: ProductListChangeProductRequestDto) {
    const mappedPatchItems = extractPatchItemsPayload(patchItems);

    return this.http.patch<{
      addedItems: ProductReferenceDto[];
      deletedItems: ProductReferenceDto[];
    }>(`${this.resource}/${id}/items`, {
      ...mappedPatchItems,
      productListId: id,
      orderType,
      stylesOnly,
    });
  }

  public changeGroupingType(
    listId: string,
    type: ProductListGroupingType,
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.resource}/${listId}/grouping/${type}`,
      {},
    );
  }

  public createListWithProducts({
    accountId,
    siteId,
    ...body
  }: ProductListBaseAddRequestDto) {
    return this.create({
      accountId,
      siteId,
      ...body,
    });
  }

  public clone({ accountId, siteId, ...body }: ProductListCloneRequestDto) {
    const request$ = this.http.post<ProductListResponseDto>(
      `${this.resource}/${body.id}/clone`,
      body,
    );
    return this.listWithUsers(request$, accountId, siteId ?? '');
  }

  public bulkDelete({
    ids,
    deleteFromLookBook = false,
  }: ProductListBulkDeleteRequestDto) {
    return this.http.delete<ProductListBulkDeleteResponseDto>(
      `${this.resource}/bulk?deleteFromLookBook=${deleteFromLookBook}`,
      { body: ids },
    );
  }

  public delete({
    id,
    deleteFromLookBook = false,
  }: ProductListDeleteRequestDto) {
    return this.bulkDelete({ ids: [id], deleteFromLookBook });
  }

  public reorderProduct({
    productListId,
    brandId,
    productId,
    order,
  }: ProductListReOrderProductRequestDto) {
    return this.http.patch<void>(
      `${this.resource}/${productListId}/brand/${brandId}/product/${productId}/reorder/${order}`,
      {},
    );
  }

  public setActive(id: string) {
    return this.http.patch<void>(`${this.resource}/${id}/setActive`, {});
  }

  public getListProductReferences(
    productListId: string,
  ): Observable<ProductReferenceDto[]> {
    return this.http
      .get<ProductListGetItemsResponseDto>(
        `${this.resource}/${productListId}/items`,
      )
      .pipe(map(({ items }) => items ?? []));
  }

  public getListsByIds(listIds: string[]) {
    return this.http
      .post<IList[]>(`${this.resource}/bulk`, {
        listIds,
      })
      .pipe(map(mapListByIdsToLegacy));
  }

  public bulkAddProducts({
    listId,
    query,
  }: BulkAddProductsRequestDto): Observable<ProductListResponseDto | string> {
    const searchParamsMap = new URLSearchParams(query);
    const {
      collapse,
      sortDirection,
      sortField,
      f,
      q,
      type,
      subsites,
      wsp,
      rrp,
      inventoryRange,
    } = Object.fromEntries(
      searchParamsMap as unknown as IterableIterator<[string, string]>,
    );

    const payload: BulkAddProductsQuery = {
      type: Object.values(PulseOrderType).some(
        (key) => key !== PulseOrderType.None && key === type,
      )
        ? type
        : PulseOrderType.Any,
      query: q ?? '',
      facets: f ? mapFiltersFacets(f) : [],
      collapse: JSON.parse(collapse),
      sortDirection: sortDirection.toLowerCase(),
      sortField: sortField.toLowerCase(),
      inventoryRange,
      wsp,
      rrp,
    };

    if (subsites) {
      payload.subsites = subsites.split(',');
    }

    return this.http.post<ProductListResponseDto>(
      `${this.resource}/${listId}/search`,
      payload,
    );
  }

  private listWithUsers<T extends ProductListResponseDto>(
    request$: Observable<T>,
    accountId: string = '',
    siteId: string = '',
  ) {
    return request$.pipe(
      switchMap((resp) =>
        this.listUsersService
          .loadAndWaitForUserEntities(resp, [resp.createdByUserId])
          .pipe(first(({ userEntities }) => userEntities.length > 0)),
      ),
      switchMap(({ userEntities, resp }) =>
        this.productsAvailabilityApi
          .loadAvailabilities({ lists: [resp], accountId, siteId })
          .pipe(
            map((items) => ({
              userEntities,
              resp: {
                ...resp,
                ...items?.[0],
              },
            })),
          ),
      ),
      map(({ userEntities, resp }) =>
        mapEngagementListToLegacy(resp, userEntities),
      ),
    );
  }
}
